/**
 * TourProvider — context + state machine for the v2 walkthrough tour
 * (#1261 Phase 5 + #1262 Phase 6).
 *
 * Mount this provider near the App root, but inside <Router> so the
 * useNavigate inside Coachmark works. Visibility is controlled by the
 * provider:
 *   - Reads /auth/onboarding (already fetched by DashboardPageV2 etc.)
 *     via the parent component's `onboardingStatus` prop.
 *   - Renders SpotlightOverlay + Coachmark when there's an active step
 *     for the current phase AND the kill-switch is on.
 *
 * Mid-tour kill-switch unmount (Gemini P1): if `onboardingV2Enabled`
 * flips false during a session (admin disables the flag), the tour
 * goes dark within one render — no error, no in-flight POST. Children are
 * never remounted by a flip in either direction (#4063).
 *
 * State writes go through a stable `restartTour()` action exposed via
 * context. POST helper is supplied by the parent (so we don't fork the
 * fetch logic from DashboardPageV2 and risk drift).
 *
 * Phase-2 trigger (the `AssessmentResults` mount → step 3 fade-in)
 * lives in this file too — Phase 6 imports a `useTriggerPhase2()` hook
 * to call from inside `AssessmentResults`. The hook reads tour state
 * from this context and writes `tour_phase = 'post_scenario'` after an
 * 800ms delay so the learner reads their score first.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { posthog } from '../../../main';
import { findAnchor, isAnchorRendered, resolveStep, TOTAL_STEPS } from './stepRegistry';
import SpotlightOverlay from './SpotlightOverlay';
import Coachmark from './Coachmark';
import { mediaQueryMatches } from '../../../lib/safeMatchMedia';
import './Tour.css';

const TourContext = createContext(null);

export function useTour() {
  const ctx = useContext(TourContext);
  // Allow `useTour()` outside a provider to return null safely — consumers
  // that opt-in to the tour (e.g. nav items adding data-tour-anchor) should
  // tolerate `null`. Throwing would couple every layout component to the
  // provider unnecessarily.
  return ctx;
}

const PHASE2_DELAY_MS = 800;

// Module scope on purpose (evaluated at import); #4215: a throwing matchMedia
// must not fail the import, so it goes through the throw-safe helper.
const PREFERS_REDUCED_MOTION = mediaQueryMatches('(prefers-reduced-motion: reduce)', false);

export function TourProvider({
  children,
  enabled,
  onboardingStatus,
  postOnboardingState,
  onLocalStateChange,
  onStartWelcomeScenario,
}) {
  // Safety-first: if v2 is off, the engine is fed no tour state, so no step
  // resolves, no effect writes, no overlay renders, and useTour() is null.
  // Mid-session flip from true→false: the overlay/coachmark unmount on the
  // very next render (no in-flight write).
  //
  // The engine is ALWAYS rendered, never swapped for a bare Provider when
  // disabled (#4063). The flag flips false→true when sign-in loads a v2
  // org's branding, and returning a different element type here made React
  // unmount and remount the WHOLE app subtree under this provider. On
  // /auth/verify the remounted VerifyPage found its magic-link token already
  // spent and flashed "Invalid magic link" until the first instance's
  // redirect timer fired. Keep `children` at one stable tree position.
  return (
    <TourEngine
      enabled={enabled === true}
      onboardingStatus={enabled ? onboardingStatus : null}
      postOnboardingState={postOnboardingState}
      onLocalStateChange={onLocalStateChange}
      onStartWelcomeScenario={onStartWelcomeScenario}
    >
      {children}
    </TourEngine>
  );
}

function TourEngine({
  children,
  enabled,
  onboardingStatus,
  postOnboardingState,
  onLocalStateChange,
  onStartWelcomeScenario,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Tour state is NULL by default — tour only fires when the learner has
  // explicitly opted in (welcome modal "Take a quick tour" CTA seeds
  // tour_step + tour_phase). Defaulting to step=1 / phase='pre_scenario'
  // auto-fires the tour on every learner's first dashboard render, which
  // is wrong: a learner who chose Skip or Try-scenario shouldn't see the
  // spotlight overlay.
  const tourStep = onboardingStatus?.tour_step ?? null;
  const tourPhase = onboardingStatus?.tour_phase ?? null;
  const tourDismissed = onboardingStatus?.tour_dismissed === true;
  const welcomeRunId = onboardingStatus?.welcome_run_id ?? null;
  const tourCompleted = tourPhase === 'complete';
  const welcomeModalActive = onboardingStatus?.show_welcome_modal === true;

  // #2796: whether the debrief currently live is `artifact_review` — the
  // only thing that ever sets this is ChatPage's reportDebriefMode() call
  // (see below), made from the SAME place the debrief-feedback anchor for
  // step 2 renders, off the SAME `run?.version?.debrief_mode` signal that
  // page's isArtifactReview already is. Reset on restartTour() so a
  // learner's second walkthrough (different scenario) doesn't inherit a
  // stale flag from the first.
  const [isArtifactReviewDebrief, setIsArtifactReviewDebrief] = useState(false);
  const reportDebriefMode = useCallback((isArtifactReview) => {
    setIsArtifactReviewDebrief(!!isArtifactReview);
  }, []);

  // Active step is the registered step for the current step number, but
  // ONLY if its phase matches the stored phase. Steps are gated by phase
  // so we don't accidentally render step 2 (debrief) before the learner
  // has done the welcome scenario.
  //
  // Also nulled when tour state hasn't been seeded yet OR while the
  // welcome modal is the active dialog — the modal and the tour overlay
  // shouldn't compete for the learner's attention. The tour will mount
  // after the modal is dismissed via "Take a quick tour".
  const activeStep = useMemo(() => {
    if (!tourStep || !tourPhase) return null;
    if (welcomeModalActive) return null;
    const step = resolveStep(tourStep, { isArtifactReviewDebrief });
    if (!step) return null;
    if (step.phase !== tourPhase) return null;
    return step;
  }, [tourStep, tourPhase, welcomeModalActive, isArtifactReviewDebrief]);

  // Anchor resolution — re-queries the DOM whenever the step changes or
  // the route changes. We poll briefly because the welcome-scenario card
  // can mount asynchronously (assignments fetch). Stop polling after the
  // step resolves OR after ~5s.
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorMissing, setAnchorMissing] = useState(false);
  useEffect(() => {
    // CRITICAL: clear anchorEl immediately on step change. Without this,
    // the spotlight stays stuck on the old element while the coachmark
    // already shows the new step's copy — a confusing "wrong highlight"
    // bug surfaced in human testing. Polling then sets the new anchor
    // when it appears, or leaves anchorEl null (hiding overlay) if the
    // anchor never appears for this step on this route.
    setAnchorEl(null);
    if (!activeStep || tourDismissed || tourCompleted) {
      setAnchorMissing(false);
      return undefined;
    }
    setAnchorMissing(false);
    let attempts = 0;
    const scrollRetryTimeouts = [];
    const tick = () => {
      const el = findAnchor(activeStep.anchor);
      if (el && !isAnchorRendered(el)) {
        // #3475 Q2874-1: the only match is in the DOM but `display:none`
        // (step 4's four-act header "Exit" button on a phone, until Act 4's
        // "Done" mounts). That is "waiting", not "missing": drawing the
        // coachmark on a zero rect is the bug, and counting it toward the
        // cap would auto-skip step 4 without its navigate('/dashboard'),
        // cascading steps 5-8 into anchor_skips on /chat. Keep polling at the
        // same interval, uncapped; the effect cleanup ends it on step change,
        // dismissal, completion or unmount, and a genuinely absent anchor
        // (el === null, e.g. the learner left /chat) still hits the cap.
        timeout = window.setTimeout(tick, 250);
        return;
      }
      if (el) {
        setAnchorEl(el);
        // Bring the anchor into view — without this, the tour fires
        // below the fold on tall dashboards and the learner thinks
        // the tour didn't start (#1261 bug). Repeated for ~1s after
        // first found to catch late layout shifts (e.g. dashboard's
        // "More" section finishes mounting after our initial call).
        const inViewport = () => {
          const r = el.getBoundingClientRect();
          return r.top >= 0 && r.bottom <= window.innerHeight;
        };
        const reduced = mediaQueryMatches('(prefers-reduced-motion: reduce)', false);
        const scrollOnce = () => {
          if (!inViewport()) {
            el.scrollIntoView({
              block: 'nearest',
              inline: 'nearest',
              behavior: reduced ? 'auto' : 'smooth',
            });
          }
        };
        scrollOnce();
        // Re-check at 300/600/900ms — covers layout reflows from
        // late-mounting siblings and smooth-scroll interruptions.
        const scrollRetries = [300, 600, 900].map(delay =>
          window.setTimeout(scrollOnce, delay)
        );
        scrollRetryTimeouts.push(...scrollRetries);
        return;
      }
      attempts += 1;
      if (attempts > 20) {
        // ~5s of polling. Give up so a missing anchor doesn't block
        // forever; we surface as a soft skip (learner can advance via
        // Next or Esc).
        setAnchorMissing(true);
        return;
      }
      timeout = window.setTimeout(tick, 250);
    };
    let timeout = window.setTimeout(tick, 0);
    return () => {
      window.clearTimeout(timeout);
      scrollRetryTimeouts.forEach(id => window.clearTimeout(id));
    };
  }, [activeStep, tourDismissed, tourCompleted]);

  // Phase-2 fade-in: when phase transitions to post_scenario from
  // mid_scenario, hide the coachmark for 800ms before showing step 3.
  // Tracks the last seen phase so we don't re-fire the delay on remount.
  const [phase2DelayActive, setPhase2DelayActive] = useState(false);
  const lastSeenPhaseRef = useRef(tourPhase);
  useEffect(() => {
    const prev = lastSeenPhaseRef.current;
    lastSeenPhaseRef.current = tourPhase;
    if (prev === 'mid_scenario' && tourPhase === 'post_scenario') {
      setPhase2DelayActive(true);
      const id = window.setTimeout(
        () => setPhase2DelayActive(false),
        PREFERS_REDUCED_MOTION ? 0 : PHASE2_DELAY_MS
      );
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [tourPhase]);

  // PostHog: fire `onboarding_tour_step_shown` once per (step, phase) tuple.
  const shownStepsRef = useRef(new Set());
  useEffect(() => {
    if (!activeStep || tourDismissed || tourCompleted || phase2DelayActive) return;
    if (!anchorEl) return;
    const key = `${activeStep.step}:${activeStep.phase}`;
    if (shownStepsRef.current.has(key)) return;
    shownStepsRef.current.add(key);
    posthog?.capture?.('onboarding_tour_step_shown', {
      step: activeStep.step,
      phase: activeStep.phase,
    });
  }, [activeStep, anchorEl, tourDismissed, tourCompleted, phase2DelayActive]);

  // Persist server-side. Errors are non-fatal — local state advances so
  // the UI never blocks on a stale POST. Sentry-grade observability is
  // outside the scope of the tour engine's per-click writes.
  const writeState = useCallback(
    async (partial, options = {}) => {
      // Optimistically apply locally first so the next render sees the new
      // step / phase / dismissed flag without waiting for the server.
      if (onLocalStateChange) onLocalStateChange(partial);
      try {
        await postOnboardingState(partial);
      } catch (err) {
        if (!options.silent) {
          console.warn('[Tour] state write failed:', err);
        }
        // Route to Sentry — without this, server rejects (e.g. Zod max drift
        // against the registry length) silently desync client and server
        // tour state. Optimistic local advances; server stays at prior step;
        // refresh loses the advance.
        Sentry.captureException(err, {
          tags: { errorId: 'ONBOARDING_TOUR_STATE_WRITE_FAILED' },
          extra: { partial },
        });
      }
    },
    [postOnboardingState, onLocalStateChange]
  );

  const advanceStep = useCallback(
    (via = 'next') => {
      if (!activeStep) return;
      posthog?.capture?.('onboarding_tour_step_advanced', {
        step: activeStep.step,
        phase: activeStep.phase,
        via,
      });

      // Step 1 (pre_scenario) primary action: start the welcome scenario.
      // Tour transition to mid_scenario happens inside
      // startWelcomeScenario along with the run creation + navigation,
      // so the three actions stay atomic. Without this, clicking the
      // tour's primary button advanced tour state but left the learner
      // stranded on the dashboard with no scenario running.
      if (activeStep.phase === 'pre_scenario') {
        if (onStartWelcomeScenario) {
          onStartWelcomeScenario({ source: 'tour_step1' });
        } else {
          // Fallback if context wasn't provided — shouldn't happen.
          writeState({ tour_step: 2, tour_phase: 'mid_scenario' });
        }
        return;
      }
      // Last debrief step (step 4 — debrief-close) → advance AND navigate
      // to /dashboard directly. This bypasses the pre-existing "What's
      // Next?" modal that the X button would otherwise surface — that
      // modal is a known derail for tour-mid learners (F4 in #1271).
      // Clicking the X opens that modal, but the coachmark's primary
      // button uses this clean exit instead.
      if (activeStep.step === 4 && activeStep.phase === 'post_scenario') {
        writeState({ tour_step: 5 });
        navigate('/dashboard');
        return;
      }
      // Last step overall → complete. Also scroll the dashboard back to the
      // top and pulse-highlight the checklist tile so the learner lands on
      // a clear next action instead of wherever the last nav step left them.
      if (activeStep.step === TOTAL_STEPS) {
        writeState({ tour_step: TOTAL_STEPS, tour_phase: 'complete' });
        posthog?.capture?.('onboarding_tour_completed', {});
        if (typeof window !== 'undefined') {
          const reduced = mediaQueryMatches('(prefers-reduced-motion: reduce)', false);
          window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
          document.body.classList.add('tour-just-completed');
          window.setTimeout(() => {
            document.body.classList.remove('tour-just-completed');
          }, 3200);
        }
        return;
      }
      writeState({ tour_step: activeStep.step + 1 });
    },
    [activeStep, writeState, onStartWelcomeScenario, navigate]
  );

  const goBack = useCallback(() => {
    if (!activeStep || activeStep.step <= 1) return;
    writeState({ tour_step: activeStep.step - 1 });
  }, [activeStep, writeState]);

  const dismiss = useCallback(() => {
    if (!activeStep) return;
    posthog?.capture?.('onboarding_tour_skipped', { step: activeStep.step });
    writeState({ tour_dismissed: true });
  }, [activeStep, writeState]);

  const restartTour = useCallback(
    (source = 'tour') => {
      shownStepsRef.current.clear();
      lastSeenPhaseRef.current = 'pre_scenario';
      // #2796: clear the reported debrief mode too — a restart re-runs the
      // pre_scenario steps against whatever scenario the learner picks next,
      // which may not be the artifact_review one that set this flag.
      setIsArtifactReviewDebrief(false);
      posthog?.capture?.('onboarding_tour_restarted', { source });
      writeState({
        tour_step: 1,
        tour_phase: 'pre_scenario',
        tour_dismissed: false,
      });
    },
    [writeState]
  );

  // #1352: auto-skip a step whose anchor never resolves.
  //
  // Before this, the anchor-missing path only flipped `anchorMissing` true and
  // rendered <DebugAnchorMissing> (logs to Sentry/PostHog, returns null). The
  // SpotlightOverlay is `: null` while anchorMissing, so the tour rendered
  // NOTHING and `tour_step` never advanced — the tour silently dead-ended on
  // the affected route (the Sentry warning's real user impact; the "Tour will
  // skip" comment was aspirational — no skip logic existed).
  //
  // Root fix: when the anchor is persistently missing, advance the tour
  // pointer past this step. We deliberately do NOT route through advanceStep —
  // its phase handlers fire side-effects (step-1 pre_scenario →
  // onStartWelcomeScenario, step-4 → navigate). A missing anchor means we are
  // NOT in this step's intended context (the Sentry case: step-1's dashboard
  // card missing while the learner is on /chat), so performing its side-effect
  // would be wrong. Pure pointer advance only; last step → complete.
  //
  // Loop-safe: each fire strictly increments tour_step (or completes →
  // activeStep becomes null, guarded below). The anchor-poll effect resets
  // anchorMissing=false on every activeStep change, so this fires at most once
  // per step; the ref guard is belt-and-braces against StrictMode double-invoke.
  const autoSkippedStepRef = useRef(null);
  useEffect(() => {
    if (!anchorMissing || !activeStep || tourDismissed || tourCompleted) return;
    const skipKey = `${activeStep.step}:${activeStep.phase}`;
    if (autoSkippedStepRef.current === skipKey) return;
    autoSkippedStepRef.current = skipKey;
    posthog?.capture?.('onboarding_tour_anchor_skipped', {
      step: activeStep.step,
      phase: activeStep.phase,
      anchor: activeStep.anchor,
    });
    if (activeStep.step === TOTAL_STEPS) {
      writeState({ tour_step: TOTAL_STEPS, tour_phase: 'complete' });
      posthog?.capture?.('onboarding_tour_completed', { via: 'anchor_skip' });
      return;
    }
    writeState({ tour_step: activeStep.step + 1 });
  }, [anchorMissing, activeStep, tourDismissed, tourCompleted, writeState]);

  // Phase-2 trigger: fire when AssessmentResults mounts AND the run id
  // matches welcome_run_id AND phase is mid_scenario. Generic "any
  // debrief" is too coarse (Codex P1.1).
  //
  // Step number 2 (not 3) — when the voice-toggle step was dropped from
  // the v1 registry, post_scenario steps shifted: spec-step-3 (score)
  // became registry-step-2. Earlier code wrote 3 here and silently
  // skipped the score-panel coachmark.
  //
  // ID COMPARISON IS STRING-COERCED ON BOTH SIDES, and that is load-bearing
  // (review-fix on PR #2798, finding 1). `welcomeRunId` is an INTEGER — the
  // backend validates it with `Number.isInteger(partial.welcome_run_id)`
  // (AuthService.updateOnboardingState) and `onboardingState.js` types it
  // `z.number().int().positive()`, so it comes back out of JSONB as a number.
  // The callers do NOT agree on the type they pass in: HelpPanelCoachingView
  // hands AssessmentResults `run?.id` (a number, so that site worked), while
  // ChatPage's hoisted #1677/#2390 effect and ChatPage's own AssessmentResults
  // both pass the `runId` from `useParams()`, which is ALWAYS a string. A
  // strict `!==` therefore made `'4321' !== 4321` true and returned early, so
  // the two ChatPage sites could never fire: on the artifact_review path
  // (#2390) that hoisted effect is the ONLY trigger — AssessmentResults does
  // not mount there — and the tour stalled at step 1 forever with no error.
  // ChatPage already knows this gap and works around it locally
  // (`String(onboarding.status.welcome_run_id) === String(runId)`); this is
  // the same comparison done ONCE at the root instead of per call site.
  const triggerPhase2 = useCallback(
    (currentRunId) => {
      if (!welcomeRunId || tourCompleted || tourDismissed) return;
      if (currentRunId == null) return;
      if (String(currentRunId) !== String(welcomeRunId)) return;
      if (tourPhase !== 'mid_scenario') return;
      posthog?.capture?.('onboarding_tour_phase_resumed', { phase: 'post_scenario' });
      writeState({ tour_step: 2, tour_phase: 'post_scenario' });
    },
    [welcomeRunId, tourCompleted, tourDismissed, tourPhase, writeState]
  );

  const contextValue = useMemo(
    () => ({
      tourStep,
      tourPhase,
      tourDismissed,
      tourCompleted,
      welcomeRunId,
      activeStep,
      restartTour,
      advanceStep,
      goBack,
      dismiss,
      triggerPhase2,
      reportDebriefMode,
    }),
    [
      tourStep,
      tourPhase,
      tourDismissed,
      tourCompleted,
      welcomeRunId,
      activeStep,
      restartTour,
      advanceStep,
      goBack,
      dismiss,
      triggerPhase2,
      reportDebriefMode,
    ]
  );

  // Visibility:
  //   - active step exists for current phase
  //   - learner hasn't dismissed
  //   - tour isn't complete
  //   - we have an anchor element
  //   - phase-2 fade-in delay isn't active
  const showOverlay =
    !!activeStep &&
    !tourDismissed &&
    !tourCompleted &&
    !!anchorEl &&
    !phase2DelayActive;

  return (
    <TourContext.Provider value={enabled ? contextValue : null}>
      {children}
      {showOverlay ? (
        <SpotlightOverlay
          anchorEl={anchorEl}
          adjacent={activeStep.adjacent === true}
          interactive={activeStep.interactive === true}
        >
          <Coachmark
            step={activeStep}
            currentStepNumber={activeStep.step}
            totalSteps={TOTAL_STEPS}
            anchorEl={anchorEl}
            placement={activeStep.placement}
            onNext={() => advanceStep('next')}
            onBack={goBack}
            onSkip={dismiss}
            isFirst={activeStep.step === 1}
            isLast={activeStep.step === TOTAL_STEPS}
            primaryLabel={
              activeStep.phase === 'pre_scenario'
                ? t('onboarding.tour.startScenario', { defaultValue: 'Start scenario →' })
                : (activeStep.step === 4 && activeStep.phase === 'post_scenario')
                  ? t('onboarding.tour.backToDashboard')
                  : null
            }
          />
        </SpotlightOverlay>
      ) : null}
      {anchorMissing && activeStep ? (
        <DebugAnchorMissing step={activeStep.step} anchor={activeStep.anchor} />
      ) : null}
    </TourContext.Provider>
  );
}

/**
 * Surfaces anchor-missing events to PostHog (for funnel analysis) + Sentry
 * (for spike alerts) + dev console. Pre-#1256-PR review, the timeout only
 * console.warn'd, so production users hitting "tour just doesn't render"
 * was invisible until pilot interviews surfaced it weeks later.
 */
function DebugAnchorMissing({ step, anchor }) {
  useEffect(() => {
    console.warn(
      `[Tour] anchor "${anchor}" not found for step ${step} after 5s. Tour will skip.`
    );
    posthog?.capture?.('onboarding_tour_anchor_missing', {
      step,
      anchor,
      pathname: typeof window !== 'undefined' ? window.location.pathname : null,
    });
    Sentry.captureMessage(`Tour anchor missing: ${anchor} (step ${step})`, {
      level: 'warning',
      tags: { errorId: 'ONBOARDING_TOUR_ANCHOR_MISSING' },
      extra: {
        step,
        anchor,
        pathname: typeof window !== 'undefined' ? window.location.pathname : null,
      },
    });
  }, [step, anchor]);
  return null;
}
